import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "sql",
  name: "SQL",
  nameEn: "SQL",
  seo: {
    title: "SQLを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "SQLはデータベース操作の共通言語。エンジニアだけでなくビジネス職でも需要が高いスキルです。ゆめさくエンジニアで実践的に学びましょう。",
    url: "/columns/sql-programming-school",
  },
  hero: {
    title: "SQLを学べる女性向けプログラミングスクール",
    englishTitle: "Learn SQL",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "SQL" },
  ],
  overview: {
    heading: "SQLとは？",
    paragraphs: [
      "SQL（Structured Query Language）は、データベースを操作するための言語です。データの検索・追加・更新・削除といった基本操作から、複雑な集計やデータ分析まで対応できます。",
      "MySQLやPostgreSQLなどのリレーショナルデータベース（RDB）で広く使われており、Web開発・データ分析・業務システムなど、IT業界のほぼすべての分野で必要とされるスキルです。",
      "エンジニアだけでなく、マーケターや事業企画などビジネス職でもSQLを使ってデータを分析する場面が増えており、職種を問わず役立つ汎用スキルとして注目されています。",
    ],
    useCases: [
      {
        label: "データ検索・集計",
        description:
          "売上データ、ユーザー行動データなどをSQLで抽出し、ビジネス上の意思決定に活用します。",
      },
      {
        label: "Webアプリのデータ管理",
        description:
          "ユーザー情報、商品情報、注文履歴など、Webアプリケーションのデータベース設計と操作を担います。",
      },
      {
        label: "レポート作成・BI連携",
        description:
          "SQLでデータを抽出してBIツール（Tableau、Lookerなど）に連携し、可視化レポートを作成します。",
      },
      {
        label: "データパイプライン構築",
        description:
          "ETL処理やデータウェアハウスの構築にSQLは不可欠。大量データの加工・整形を効率的に行います。",
      },
    ],
  },
  whyLearn: {
    heading: "SQLを学ぶべき3つの理由",
    items: [
      {
        title: "どの職種でも使える万能スキル",
        description:
          "バックエンドエンジニア、データアナリスト、マーケター、プロダクトマネージャーなど、データに関わるすべての職種でSQLは求められます。一度習得すれば、キャリアの選択肢が大きく広がります。",
      },
      {
        title: "学習コストが低く、成果が出やすい",
        description:
          "SQLの基本文法はSELECT、FROM、WHEREなど英語に近い直感的な構文です。数日の学習で基本的なデータ抽出ができるようになり、すぐに実務で活用できる即効性のあるスキルです。",
      },
      {
        title: "データドリブンな時代に不可欠",
        description:
          "DX推進により、データに基づいた意思決定がすべての企業で重視されています。SQLはそのデータを自分の手で取り出すための第一歩であり、データリテラシーの基盤となるスキルです。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでSQLを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではWeb開発の中でSQLを実践的に学びます。単なるクエリの書き方だけでなく、データベース設計の考え方や、Webアプリケーションとの連携方法まで幅広くカバーします。",
      "実際のデータセットを使った分析課題を通じて、ビジネスに活きるSQL力を養います。",
    ],
    topics: [
      "SELECT文の基本（WHERE・ORDER BY・LIMIT）",
      "集計関数（COUNT・SUM・AVG・GROUP BY）",
      "テーブル結合（JOIN・LEFT JOIN・サブクエリ）",
      "データベース設計の基礎（正規化・ER図）",
      "インデックスとパフォーマンスチューニング",
      "実践プロジェクト：ECサイトの売上分析レポート作成",
    ],
  },
  careerPaths: {
    heading: "SQLを活かせるキャリア",
    items: [
      {
        title: "データアナリスト",
        description:
          "SQLを使って企業のデータを抽出・分析し、レポートやダッシュボードを作成する職種です。ビジネスの意思決定を数値で支える重要な役割を担います。",
        salary: "400〜650万円",
      },
      {
        title: "データベースエンジニア",
        description:
          "データベースの設計・構築・運用・チューニングを専門とするエンジニアです。大規模システムの安定稼働を支え、パフォーマンス改善に貢献します。",
        salary: "500〜800万円",
      },
      {
        title: "バックエンドエンジニア",
        description:
          "WebアプリケーションのAPI開発とデータベース連携を担当します。SQLの深い知識があることで、効率的なクエリ設計や適切なDB設計ができるエンジニアとして評価されます。",
        salary: "450〜700万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "2〜4ヶ月（基本文法のみ）",
        luagate: "1〜2ヶ月（実務レベルまで一気に習得）",
      },
      {
        label: "挫折率",
        selfStudy: "JOINやサブクエリで複雑になり挫折しやすい",
        luagate: "実データを使った演習で段階的に理解",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "教材のサンプルデータのみで実感が薄い",
        luagate: "実際のビジネスデータに近い課題で実践力を養成",
      },
      {
        label: "質問対応",
        selfStudy: "クエリの効率的な書き方がわからない",
        luagate: "メンターがパフォーマンスを意識した書き方を指導",
      },
      {
        label: "就職サポート",
        selfStudy: "SQLスキルの証明方法がわからない",
        luagate: "分析レポートをポートフォリオとして就活に活用",
      },
    ],
  },
  faq: [
    {
      question: "SQLはプログラミング言語ですか？",
      answer:
        "SQLはデータベース操作に特化した「問い合わせ言語」であり、一般的なプログラミング言語とは少し異なります。しかし、IT業界で必須のスキルであり、PythonやJavaScriptと組み合わせて使うことが多いです。",
    },
    {
      question: "エンジニア以外でもSQLは必要ですか？",
      answer:
        "はい、データ分析やマーケティング職でもSQLを使う機会が増えています。SQLを使えると、エンジニアに依頼しなくても自分でデータを取得・分析できるため、業務効率が大幅に向上します。",
    },
    {
      question: "MySQLとPostgreSQLのどちらを学べばいいですか？",
      answer:
        "基本的なSQLの文法はどちらも同じです。ゆめさくエンジニアではPostgreSQLを使用していますが、学んだ知識はMySQLやその他のRDBでもそのまま活用できます。",
    },
    {
      question: "SQLの学習にどれくらい時間がかかりますか？",
      answer:
        "基本的なSELECT文は数日で書けるようになります。JOINやサブクエリなど実務レベルのスキルは1〜2ヶ月で習得可能です。他の言語に比べて学習コストが低く、短期間で成果を実感できるのがSQLの魅力です。",
    },
  ],
  relatedLanguages: [
    { slug: "python", name: "Python" },
    { slug: "javascript", name: "JavaScript" },
    { slug: "html-css", name: "HTML/CSS" },
    { slug: "react", name: "React" },
  ],
};
